import type {PDFDocumentLoadingTask, PDFDocumentProxy} from 'pdfjs-dist';
import {Alert, Button, DataStatus, useTranslationContext} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {useOnMount} from 'shared/web';

import type {PdfPreviewPageInfo} from '../../../types/PdfPreviewPageInfo';
import {PdfPreviewDocument} from './PdfPreviewDocument';

type PreviewState =
  | {status: 'loading'}
  | {status: 'error'}
  | {status: 'ready'; document: PDFDocumentProxy; pages: PdfPreviewPageInfo[]};
type PdfPreviewLoaderProps = {bytes: Uint8Array; onRetry: () => void};

// PDF points are 1/72 inch; CSS pixels are 1/96 inch. PDF.js applies page rotation.
const PDF_TO_CSS_SCALE = 96 / 72;

export function PdfPreviewLoader(props: PdfPreviewLoaderProps) {
  const t = useTranslationContext();
  const [state, setState] = useState<PreviewState>({status: 'loading'});
  const loadingTask = useRef<PDFDocumentLoadingTask | null>(null);
  const activeController = useRef<AbortController | null>(null);

  useOnMount(
    () => {
      const controller = new AbortController();
      activeController.current = controller;
      void import('../../../utils/loadPdfPreview')
        .then(({loadPdfPreview}) => loadPdfPreview())
        .then((pdfjs) => {
          controller.signal.throwIfAborted();
          // PDF.js transfers the input buffer. Keep the generated bytes intact for reopening.
          const task = pdfjs.getDocument({data: Uint8Array.from(props.bytes)});
          loadingTask.current = task;
          return task.promise.then(async (document) => {
            controller.signal.throwIfAborted();
            // Reserve each page's actual aspect ratio before mounting any canvases.
            const pages = await Promise.all(
              Array.from({length: document.numPages}, async (_, index) => {
                const page = await document.getPage(index + 1);
                controller.signal.throwIfAborted();
                const viewport = page.getViewport({scale: PDF_TO_CSS_SCALE});
                return {pageNumber: index + 1, width: viewport.width, height: viewport.height};
              })
            );
            if (!controller.signal.aborted) {
              setState({status: 'ready', document, pages});
            }
          });
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setState({status: 'error'});
          }
        });
    },
    () => {
      activeController.current?.abort();
      void loadingTask.current?.destroy();
    }
  );

  return match(state)
    .with({status: 'loading'}, () => <DataStatus isLoading minHeight={50} />)
    .with({status: 'error'}, () => (
      <Box padding={4}>
        <VStack spacing={4}>
          <Alert variant="error" type="inline" message={t('general.labels.somethingWentWrong')} />
          <HStack>
            <Button title={t('general.actions.retry')} onClick={props.onRetry} />
          </HStack>
        </VStack>
      </Box>
    ))
    .with({status: 'ready'}, ({document, pages}) => (
      <PdfPreviewDocument document={document} pages={pages} />
    ))
    .exhaustive();
}
